import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InvoiceReportPeriod } from '@tenfour/contracts'
import { InvoiceFilters, InvoiceStatus } from '@/lib/types'
import {
  listInvoices,
  listAllInvoices,
  getInvoice,
  getPaymentsReceived,
  createInvoice,
  updateInvoice,
  updateInvoiceStatus,
  approveInvoice,
  unapproveInvoice,
  recordInvoicePayment,
  updateInvoicePayment,
  deleteInvoicePayment,
  type ManualPaymentMethod,
  resendInvoice,
  sendInvoiceReminder,
  voidInvoice,
  uncancelInvoice,
  convertToInvoice,
  sendQuote,
  getInvoiceReportSummary,
} from './api'

export function useInvoices(
  accountId: string,
  filters?: InvoiceFilters & { cursor?: string },
) {
  return useQuery({
    queryKey: ['invoices', accountId, filters],
    queryFn: () => listInvoices(accountId, filters),
    enabled: !!accountId,
  })
}

/** /reports figures (#574) — server aggregate; gated by View reports (#572). */
export function useInvoiceReportSummary(accountId: string, period: InvoiceReportPeriod) {
  return useQuery({
    queryKey: ['invoice-report-summary', accountId, period],
    queryFn: () => getInvoiceReportSummary(accountId, period),
    enabled: !!accountId,
  })
}

export function useAllInvoices(accountId: string) {
  return useQuery({
    queryKey: ['invoices', accountId, 'all'],
    queryFn: () => listAllInvoices(accountId),
    enabled: !!accountId,
  })
}

/**
 * One customer's invoices (lazy) — backs the customer detail page's invoices
 * card and, via paymentsFromInvoices(), its payments card. 100 covers any
 * realistic customer; paginate with the cursor if that ever stops being true.
 */
export function useCustomerInvoices(accountId: string, customerId: string | null | undefined) {
  return useQuery({
    queryKey: ['invoices', accountId, { customerId, limit: 100 }],
    queryFn: () => listInvoices(accountId, { customerId: customerId!, limit: 100 }),
    enabled: !!accountId && !!customerId,
  })
}

/** Payments-received list for the /payouts page — infinite cursor pagination. */
export function usePaymentsReceived(accountId: string) {
  return useInfiniteQuery({
    queryKey: ['invoice-payments', accountId],
    queryFn: ({ pageParam }) => getPaymentsReceived(accountId, { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!accountId,
  })
}

export function useInvoice(invoiceId: string) {
  return useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId,
    // Approval only starts delivery. Keep mounted record/Aria views current
    // until the carrier settles the attempt or failure restores the draft.
    refetchInterval: (query) => query.state.data?.status === 'SENDING' ? 2000 : false,
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createInvoice,
    onSuccess: (_invoice, variables) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      // #527: the job header's invoice action reads job.invoice — refresh the
      // single-job query so the label flips truthfully after the builder.
      if (variables.jobId) {
        queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] })
      }
    },
  })
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, updates }: { invoiceId: string; updates: Parameters<typeof updateInvoice>[1] }) =>
      updateInvoice(invoiceId, updates),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      invoiceId,
      status,
      paymentMethod,
    }: {
      invoiceId: string
      status: InvoiceStatus
      paymentMethod?: 'CASH' | 'CHECK' | 'STRIPE' | 'VENMO'
    }) => updateInvoiceStatus(invoiceId, status, paymentMethod),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useApproveInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => approveInvoice(invoiceId),
    onSuccess: (_data, invoiceId) => {
      // Refresh the invoice DETAIL (drives the approve card) as well as the list,
      // so the page reacts immediately without a manual refresh.
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUnapproveInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => unapproveInvoice(invoiceId),
    onSuccess: (_data, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useRecordPayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { invoiceId: string; method: ManualPaymentMethod; amount?: number }) =>
      recordInvoicePayment(vars.invoiceId, { method: vars.method, amount: vars.amount }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUpdatePayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { invoiceId: string; paymentId: string; method: ManualPaymentMethod; amount?: number }) =>
      updateInvoicePayment(vars.invoiceId, vars.paymentId, { method: vars.method, amount: vars.amount }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useDeletePayment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: { invoiceId: string; paymentId: string }) =>
      deleteInvoicePayment(vars.invoiceId, vars.paymentId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useResendInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => resendInvoice(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useSendInvoiceReminder() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => sendInvoiceReminder(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useVoidInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => voidInvoice(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useUncancelInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => uncancelInvoice(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      // #527: a restore from the job header flips its action back to
      // "Open invoice" — the single-job query carries job.invoice.status.
      queryClient.invalidateQueries({ queryKey: ['job'] })
    },
  })
}

export function useConvertToInvoice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => convertToInvoice(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export function useSendQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId: string) => sendQuote(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}
